import React from "react";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import { formatCurrency, formatDate } from "../helpers/formatters";
import Button from "./library/Button";
import Application from "../apiResources/Application";
import { Card, CardTitle, CardSubtitle } from "./library/Card";
import ApplicationStatusTag from "./ApplicationStatusTag";
import StatusTag from "./StatusTag";
import StatusTagGroup from "./StatusTagGroup";

type Props = {
  application: Application;
};

const ApplicationIndexCard = (props: Props) => {
  const { application } = props;

  const hasRecruiter = Boolean(application?.engagement?.recruiter);

  const salary =
    application.candidateDesiredSalary || application.candidate.desiredSalary;

  return (
    <Card key={application.id}>
      <Header>
        <div>
          <CardSubtitle>{application.job.title}</CardSubtitle>
          <CardTitle>
            {application.candidate.fullName}
            <ReactTooltip
              type="dark"
              aria-haspopup="true"
              id="salary-tooltip"
            />
            {hasRecruiter && (
              <Salary
                data-tip="Candidate's desired salary"
                data-for="salary-tooltip"
              >
                {formatCurrency(salary, {
                  currency: application.engagement.recruiter.currency.isoCode,
                })}
              </Salary>
            )}
          </CardTitle>
        </div>
        <ButtonGroup>
          <Button
            size="sm"
            tertiary
            onClick={() => (window.location.href = application.previewCvLink)}
          >
            Read CV
          </Button>
          {application.viewLink && (
            <Button
              size="sm"
              primary
              onClick={() => (window.location.href = application.viewLink)}
            >
              View Application
            </Button>
          )}
        </ButtonGroup>
      </Header>
      <Row>
        <StatusTagGroup>
          {application.feedbackOverdue ? (
            <StatusTag status="error">
              <FontAwesomeIcon icon={faExclamationTriangle} /> Feedback Overdue
            </StatusTag>
          ) : (
            <ApplicationStatusTag status={application.status} />
          )}
        </StatusTagGroup>
        <CardUpdatedAt>
          Last updated: {formatDate(application.updatedAt)}
        </CardUpdatedAt>
      </Row>
    </Card>
  );
};

const ButtonGroup = styled.div`
  text-align: right;

  button + button {
    margin-left: 1rem;
    margin-top: 1rem;
  }
`;

const Salary = styled.span`
  font-size: 14px;
  font-weight: normal;
  margin-top: 0.5rem;

  @media screen and (min-width: 600px) {
    margin-top: 0;

    margin-left: 1rem;
    padding-left: 1rem;
    border-left: 1px solid ${({ theme }) => theme.colors.gray300};
  }
`;

const CardUpdatedAt = styled.div`
  font-size: 13px;
  text-align: right;
  color: ${({ theme }) => theme.colors.muted};
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

export default ApplicationIndexCard;
